import React from 'react';

import { GetServerSideProps, NextPage } from 'next';
import { ParsedUrlQuery } from 'querystring';

import { ProductsListFilters, ProductsListSortMode } from '@domains/products-catalog';
import { CategoryPageProps, ProductPageProps, WithGlobalData } from '@domains/view';

import { BFFCookieClientClass } from '@api/client/bff';
import { ProductsDetailsPayload, ProductsListPayload } from '@api/protocol';

import { NewProductsPageSlug, PromoPageSlug } from '@utils/constants';
import { isPageOutOfRange } from '@utils/pagination';
import { defaultPage, pageSize } from '@utils/products-list/constants';
import { parseFilters, parseProductsListSortMode } from '@utils/products-list/query-parsers';
import { parsePositiveInt } from '@utils/query';

import { CategoryView } from '@views/CategoryView/CategoryView';
import { ProductView } from '@views/ProductView/ProductView';

type QueryParams = {
    sort_mode: QueryParam;
    manufacturers: QueryParam;
    countries: QueryParam;
    is_promo: QueryParam;
    is_novelty: QueryParam;
    is_ready_to_eat: QueryParam;
    min_price: QueryParam;
    max_price: QueryParam;
    page: QueryParam;
} & ParsedUrlQuery;

export const getCategoryProps: GetServerSideProps<
    WithGlobalData<CategoryPageProps>,
    QueryParams
> = async (context) => {
    const { req, params, query } = context;

    const path = params?.path ?? [];
    const slug = path.length > 0 ? path[path.length - 1] : '';

    if (slug === '') {
        return {
            notFound: true,
        };
    }

    const sortMode = parseProductsListSortMode(query.sort_mode) ?? ProductsListSortMode.Popularity;
    const page = parsePositiveInt(query.page) ?? defaultPage;
    let filters: ProductsListFilters;
    if (slug === PromoPageSlug) {
        filters = parseFilters({
            ...query,
            is_promo: 'true',
        });
    } else if (slug === NewProductsPageSlug) {
        filters = parseFilters({
            ...query,
            is_novelty: 'true',
        });
    } else {
        filters = parseFilters(query);
    }

    const categoryIdentifier =
        slug === PromoPageSlug || slug === NewProductsPageSlug ? undefined : { slug };

    const payload: ProductsListPayload = {
        sort_mode: sortMode,
        page_size: pageSize,
        page: page,
        filters,
        category_identifier: categoryIdentifier,
    };

    const result = await BFFCookieClientClass<CategoryPageProps>({
        request: req,
        isNotFoundOnErrorShown: true,
    })
        .addToStack<ProductsListPayload>({
            prop: 'data',
            url: '/api/products_catalog/products/list',
            payload,
        })
        .addToStack({
            prop: 'searchQuery',
            data: null,
        })
        .addToStack({
            prop: 'categorySlug',
            data: slug,
        })
        .addToStack({
            prop: 'sortMode',
            data: sortMode,
        })
        .addToStack({
            prop: 'filters',
            data: filters,
        })
        .addToStack<void, CategoryPageProps['pageType']>({
            prop: 'pageType',
            data: 'category',
        })
        .getServerProps();

    if ('props' in result && 'data' in result.props) {
        const { total, page_size } = result.props.data;
        if (isPageOutOfRange(page, total, page_size)) {
            return { notFound: true };
        }
    }

    return result;
};

export const getProductProps: BFF.GetPageServerSideProps<
    WithGlobalData<ProductPageProps>,
    QueryParams
> = async (context) => {
    const { req, params } = context;

    const path = params?.path ?? [];
    const slug = path.length > 0 ? path[path.length - 1] : '';
    const categorySlug = path.length > 1 ? path[path.length - 2] : '';

    if (slug === '') {
        return {
            notFound: true,
        };
    }

    return BFFCookieClientClass<ProductPageProps>({
        request: req,
        isNotFoundOnErrorShown: true,
    })
        .addToStack<ProductsDetailsPayload>({
            prop: 'data',
            url: '/api/products_catalog/products/details',
            payload: { identifier: { slug }, category_slug: categorySlug },
        })
        .addToStack<void, ProductPageProps['pageType']>({
            prop: 'pageType',
            data: 'product',
        })
        .getServerProps();
};

export const getServerSideProps: BFF.GetPageServerSideProps<
    WithGlobalData<CategoryPageProps | ProductPageProps>,
    QueryParams
> = async (context) => {
    const { params } = context;
    const path = params?.path ?? [];
    const isProductPage = path.length === 2;

    if (path.length > 2) return { notFound: true };

    let result;
    if (isProductPage) {
        result = await getProductProps(context);
    } else {
        result = await getCategoryProps(context);
    }
    return result;
};

const Page: NextPage<WithGlobalData<CategoryPageProps | ProductPageProps>> = (props) => {
    const { pageType } = props;
    if (pageType === 'product')
        return <ProductView {...(props as WithGlobalData<ProductPageProps>)} />;
    return <CategoryView {...(props as WithGlobalData<CategoryPageProps>)} />;
};

export default Page;
